/**
 * Una burbuja del chat interno, con el aspecto de WhatsApp
 * (24/9/2026). Solo cómo se ve: los
 * permisos, el borrado y el resto viven en pages/ChatPage.jsx y en el
 * servidor.
 *
 *  · Las tuyas, a la derecha y en verde claro; las de los demás, a la
 *    izquierda y en blanco (en oscuro, verde y gris oscuros con contraste).
 *  · La «colita» —la esquina de arriba recortada del lado de quien escribe—
 *    y el nombre, en color, solo en el PRIMER mensaje de una racha seguida de
 *    la misma persona; y el nombre, solo en los de los demás.
 *  · La hora, pequeña, abajo a la derecha DENTRO de la burbuja (sobre la
 *    foto, si el mensaje no lleva texto).
 *  · Las fotos, DENTRO de la burbuja y grandes: una sola ocupa el ancho de la
 *    burbuja; de 2 a 4, en cuadrícula de dos columnas; si hubiera más, «+N»
 *    sobre la última. El hueco de cada foto se reserva antes de que cargue
 *    (con sus medidas), para que la lista no dé saltos.
 *  · El texto es TEXTO: React lo escapa, los enlaces http(s) se montan aquí
 *    y los caracteres que cambian la dirección del texto no se pintan.
 */
import React from 'react';
import { Ban, Trash2 } from 'lucide-react';
import { urlFoto } from '../../lib/fotosChat';

// Los caracteres que cambian la dirección del texto (bidi). El servidor ya no
// los guarda (23/9/2026); aquí tampoco se pintan, por si quedara alguno de
// antes: con uno de ellos, un enlace se ve distinto de adonde lleva.
const DIRECCION_RE = /[؜‎‏‪-‮⁦-⁩]/g;

export function sinDireccion(texto) {
  return String(texto || '').replace(DIRECCION_RE, '');
}

export function hora(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

// Solo http y https: un «javascript:» escrito en el chat se queda en texto.
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

/** El texto con los enlaces pulsables, montados por React: nada de HTML. */
function TextoConEnlaces({ texto }) {
  const trozos = sinDireccion(texto).split(URL_RE);
  return trozos.map((trozo, i) => {
    if (i % 2 === 0) return <React.Fragment key={i}>{trozo}</React.Fragment>;
    // El punto o el paréntesis que cierra la frase no es parte del enlace.
    const [, url, cola] = trozo.match(/^(.*?)([.,;:!?)]*)$/);
    return (
      <React.Fragment key={i}>
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="underline underline-offset-2 break-all text-[#027EB5] dark:text-[#53BDEB]">{url}</a>
        {cola}
      </React.Fragment>
    );
  });
}

// Colores de las burbujas. Fijos (no `bg-white`): la casa cambia `bg-white`
// por el color de tarjeta en oscuro, y aquí cada tema tiene el suyo.
const COLOR_MIA = 'bg-[#D9FDD3] text-[#111B21] dark:bg-[#005C4B] dark:text-[#E9EDEF]';
const COLOR_AJENA = 'bg-[#FFFFFF] text-[#111B21] dark:bg-[#202C33] dark:text-[#E9EDEF]';
const COLITA_MIA = 'fill-[#D9FDD3] dark:fill-[#005C4B]';
const COLITA_AJENA = 'fill-[#FFFFFF] dark:fill-[#202C33]';
const META = 'text-[#667781] dark:text-[#8696A0]';

// El color del nombre, estable por persona (como en WhatsApp), con su
// versión legible en oscuro.
const COLORES_NOMBRE = [
  'text-[#1F7A5A] dark:text-[#06CF9C]',
  'text-[#A8327F] dark:text-[#FF72A1]',
  'text-[#1F6FB2] dark:text-[#53BDEB]',
  'text-[#B35B00] dark:text-[#FFB347]',
  'text-[#6B4FBB] dark:text-[#A791FF]',
  'text-[#0E7C86] dark:text-[#25D3E0]',
];

export function colorDeNombre(clave) {
  const texto = String(clave || '');
  let h = 0;
  for (let i = 0; i < texto.length; i += 1) h = ((h * 31) + texto.charCodeAt(i)) % 9973;
  return COLORES_NOMBRE[h % COLORES_NOMBRE.length];
}

/** La «colita»: un triángulo pegado a la esquina de arriba, del lado de
 *  quien escribe. */
function Colita({ mio }) {
  return (
    <svg viewBox="0 0 8 13" width="8" height="13" aria-hidden="true"
      className={`absolute top-0 ${mio ? '-right-2' : '-left-2'} ${mio ? COLITA_MIA : COLITA_AJENA}`}>
      <path d={mio ? 'M0 0 H8 L0 13 Z' : 'M8 0 H0 L8 13 Z'} />
    </svg>
  );
}

function limitar(valor, minimo, maximo) {
  return Math.min(maximo, Math.max(minimo, valor));
}

/** Las fotos dentro de la burbuja. `horaEncima`: la hora va sobre la última
 *  foto (mensaje sin texto). */
function Fotos({ sala, fotos, onAbrir, horaEncima }) {
  const vistas = fotos.slice(0, 4);
  const resto = fotos.length - vistas.length;
  const sola = vistas.length === 1;
  const tres = vistas.length === 3;

  const baldosa = (f, i) => {
    const ultima = i === vistas.length - 1;
    let forma;
    if (sola) {
      // Su proporción, sin pasarse ni de alargada ni de apaisada; el alto,
      // con tope (object-cover recorta lo que sobre).
      const proporcion = f.ancho && f.alto ? limitar(f.ancho / f.alto, 0.75, 1.9) : 4 / 3;
      forma = { aspectRatio: String(proporcion), maxHeight: '22rem' };
    }
    const clases = sola ? 'w-full'
      : (tres && i === 0 ? 'col-span-2 aspect-[2/1]' : 'aspect-square');
    return (
      <button key={f.id} type="button" onClick={() => onAbrir(i)}
        className={`relative block overflow-hidden bg-black/10 dark:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A884] ${clases}`}
        style={forma}
        aria-label={`Ver la foto ${i + 1} de ${fotos.length} en grande`}
        data-testid="chat-foto">
        <img src={urlFoto(sala, f.id, { miniatura: true })} alt=""
          loading="lazy" decoding="async" draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="chat-foto-img" />
        {ultima && resto > 0 && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-white text-2xl font-semibold"
            data-testid="chat-fotos-mas">+{resto}</span>
        )}
        {ultima && horaEncima && (
          <span className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
        )}
        {ultima && horaEncima && (
          <span className="absolute bottom-1 right-1.5 text-[11px] text-white tabular-nums drop-shadow"
            data-testid="chat-mensaje-hora">{horaEncima}</span>
        )}
      </button>
    );
  };

  return (
    <div className={`overflow-hidden rounded-md ${sola ? '' : 'grid grid-cols-2 gap-0.5'}`}
      data-testid="chat-fotos" data-cuantas={fotos.length}>
      {vistas.map(baldosa)}
    </div>
  );
}

export default function BurbujaChat({
  m, sala, mio, primeroDeRacha, puedeBorrar, onBorrar, onAbrirFoto,
}) {
  const fotos = (!m.borrado && Array.isArray(m.fotos)) ? m.fotos.filter((f) => f && f.id) : [];
  const conFotos = fotos.length > 0;
  const texto = m.borrado ? '' : sinDireccion(m.texto);
  const laHora = hora(m.creado_at);
  // Sin texto, la hora va sobre la foto; con texto (o eliminado), abajo.
  const horaSobreFoto = conFotos && !texto;
  const verNombre = !mio && primeroDeRacha;
  const papelera = puedeBorrar && mio && !m.borrado;

  return (
    <div className={`group flex items-center gap-1 ${mio ? 'justify-end' : 'justify-start'} ${primeroDeRacha ? 'mt-2' : 'mt-0.5'}`}
      data-testid="chat-mensaje" data-id={m.id} data-mio={mio ? 'si' : 'no'}
      data-racha={primeroDeRacha ? 'inicio' : 'sigue'}>
      {papelera && (
        // Fuera de la burbuja, a su lado: nunca tapa ni el texto ni la foto.
        // A la vista siempre, discreta; donde hay ratón, se esconde hasta
        // pasar por encima (la misma condición que usa la casa para `hover:`).
        <button type="button" onClick={() => onBorrar(m)}
          className={`shrink-0 p-1.5 rounded-full ${META} opacity-60 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover:opacity-80 hover:!opacity-100 focus:!opacity-100 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A884]`}
          title="Eliminar este mensaje" aria-label="Eliminar este mensaje"
          data-testid="chat-mensaje-borrar">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      <div className={`relative rounded-lg shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] text-[14.5px] leading-[1.35] ${mio ? COLOR_MIA : COLOR_AJENA} ${
        primeroDeRacha ? (mio ? 'rounded-tr-none' : 'rounded-tl-none') : ''} ${
        conFotos ? 'p-1 w-[min(76vw,330px)]' : 'px-2.5 pt-1.5 pb-2 max-w-[85%] sm:max-w-[70%]'}`}
        data-testid="chat-burbuja">
        {primeroDeRacha && <Colita mio={mio} />}
        {verNombre && (
          <div className={`text-[13px] font-medium leading-5 ${conFotos ? 'px-1.5 pt-0.5 pb-1' : ''} ${colorDeNombre(m.autor_id || m.autor_nombre)}`}
            data-testid="chat-mensaje-autor">
            {sinDireccion(m.autor_nombre)}
          </div>
        )}
        {m.borrado ? (
          <div className={`flex items-center gap-1.5 italic ${META} ${conFotos ? 'px-1.5' : ''}`}
            data-testid="chat-mensaje-eliminado">
            <Ban className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span>Mensaje eliminado</span>
            <span className="inline-block w-10" aria-hidden="true" />
          </div>
        ) : (
          <>
            {conFotos && (
              <Fotos sala={sala} fotos={fotos} horaEncima={horaSobreFoto ? laHora : null}
                onAbrir={(i) => onAbrirFoto(m, i)} />
            )}
            {texto && (
              <div className={`whitespace-pre-wrap break-words ${conFotos ? 'px-1.5 pt-1.5 pb-1' : ''}`}
                data-testid="chat-mensaje-texto">
                <TextoConEnlaces texto={texto} />
                {/* El sitio de la hora al final de la última línea, para que
                    la hora no se monte encima del texto. */}
                <span className="inline-block w-12 align-bottom" aria-hidden="true" />
              </div>
            )}
          </>
        )}
        {!horaSobreFoto && (
          <span className={`absolute bottom-1 right-2 text-[11px] leading-none tabular-nums ${META}`}
            data-testid="chat-mensaje-hora">{laHora}</span>
        )}
      </div>
    </div>
  );
}
